import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Settings as SettingsIcon,
    Bell,
    Mail,
    Globe,
    Palette,
    Shield,
    Save,
    Volume2,
    VolumeX,
    Sun,
    Moon,
    CheckCircle,
    RefreshCw
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useToast } from '../../components/ui/toast';

const SETTINGS_KEY = 'adminSettings';

const defaultSettings = {
    notifications: {
        emailNotifications: true,
        browserNotifications: true,
        soundEnabled: true,
        notifyOnNewMessage: true,
        notifyOnNewApplication: true,
        notifyOnNewUser: false
    },
    display: {
        darkMode: true,
        compactView: false,
        showAnimations: true,
        autoRefresh: true,
        refreshInterval: 5000
    },
    general: {
        siteName: 'Pride Path Commercial',
        adminEmail: 'admin@pridepath.lk',
        timezone: 'Asia/Colombo',
        language: 'en'
    }
};

const SettingsPage = () => {
    const [settings, setSettings] = useState(defaultSettings);
    const [saved, setSaved] = useState(false);
    const toast = useToast();

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = () => {
        try {
            const stored = localStorage.getItem(SETTINGS_KEY);
            if (stored) {
                setSettings({ ...defaultSettings, ...JSON.parse(stored) });
            }
        } catch (e) {
            console.error('Error loading settings:', e);
        }
    };

    const handleChange = (category, key, value) => {
        setSettings(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }));
        setSaved(false);
    };

    const saveSettings = () => {
        try {
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
            setSaved(true);
            toast.success('Settings saved successfully!');
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            toast.error('Failed to save settings');
        }
    };

    const resetToDefault = () => {
        if (window.confirm('Are you sure you want to reset all settings to default?')) {
            setSettings(defaultSettings);
            localStorage.setItem(SETTINGS_KEY, JSON.stringify(defaultSettings));
            toast.info('Settings reset to default');
        }
    };

    const ToggleSwitch = ({ checked, onChange, label, description }) => (
        <div className="flex items-center justify-between py-3">
            <div>
                <p className="text-white font-medium">{label}</p>
                {description && <p className="text-sm text-gray-500">{description}</p>}
            </div>
            <button
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-teal-500' : 'bg-gray-600'
                    }`}
            >
                <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
                        }`}
                />
            </button>
        </div>
    );

    return (
        <AdminLayout title="Settings">
            <Helmet>
                <title>Settings - Admin Dashboard</title>
            </Helmet>

            <div className="max-w-4xl">
                {/* Notification Settings */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                            <Bell className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Notification Settings</h3>
                            <p className="text-sm text-gray-500">Control how you receive notifications</p>
                        </div>
                    </div>

                    <div className="space-y-2 divide-y divide-white/5">
                        <ToggleSwitch
                            label="Email Notifications"
                            description="Receive notifications via email"
                            checked={settings.notifications.emailNotifications}
                            onChange={(v) => handleChange('notifications', 'emailNotifications', v)}
                        />
                        <ToggleSwitch
                            label="Browser Notifications"
                            description="Show desktop notifications in browser"
                            checked={settings.notifications.browserNotifications}
                            onChange={(v) => handleChange('notifications', 'browserNotifications', v)}
                        />
                        <ToggleSwitch
                            label="Sound Enabled"
                            description="Play sound for new notifications"
                            checked={settings.notifications.soundEnabled}
                            onChange={(v) => handleChange('notifications', 'soundEnabled', v)}
                        />
                        <ToggleSwitch
                            label="New Message Alerts"
                            description="Get notified when a new contact message arrives"
                            checked={settings.notifications.notifyOnNewMessage}
                            onChange={(v) => handleChange('notifications', 'notifyOnNewMessage', v)}
                        />
                        <ToggleSwitch
                            label="New Application Alerts"
                            description="Get notified when a new loan application is submitted"
                            checked={settings.notifications.notifyOnNewApplication}
                            onChange={(v) => handleChange('notifications', 'notifyOnNewApplication', v)}
                        />
                        <ToggleSwitch
                            label="New User Alerts"
                            description="Get notified when a new admin user is created"
                            checked={settings.notifications.notifyOnNewUser}
                            onChange={(v) => handleChange('notifications', 'notifyOnNewUser', v)}
                        />
                    </div>
                </div>

                {/* Display Settings */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <Palette className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">Display Settings</h3>
                            <p className="text-sm text-gray-500">Customize the admin interface</p>
                        </div>
                    </div>

                    <div className="space-y-2 divide-y divide-white/5">
                        <ToggleSwitch
                            label="Dark Mode"
                            description="Use dark theme for the admin dashboard"
                            checked={settings.display.darkMode}
                            onChange={(v) => handleChange('display', 'darkMode', v)}
                        />
                        <ToggleSwitch
                            label="Compact View"
                            description="Use smaller spacing and fonts"
                            checked={settings.display.compactView}
                            onChange={(v) => handleChange('display', 'compactView', v)}
                        />
                        <ToggleSwitch
                            label="Show Animations"
                            description="Enable smooth transitions and animations"
                            checked={settings.display.showAnimations}
                            onChange={(v) => handleChange('display', 'showAnimations', v)}
                        />
                        <ToggleSwitch
                            label="Auto Refresh"
                            description="Automatically refresh data in real-time"
                            checked={settings.display.autoRefresh}
                            onChange={(v) => handleChange('display', 'autoRefresh', v)}
                        />

                        {settings.display.autoRefresh && (
                            <div className="py-3">
                                <label className="text-white font-medium block mb-2">Refresh Interval</label>
                                <select
                                    value={settings.display.refreshInterval}
                                    onChange={(e) => handleChange('display', 'refreshInterval', parseInt(e.target.value))}
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                                >
                                    <option value={3000} className="bg-slate-800">3 seconds</option>
                                    <option value={5000} className="bg-slate-800">5 seconds</option>
                                    <option value={10000} className="bg-slate-800">10 seconds</option>
                                    <option value={30000} className="bg-slate-800">30 seconds</option>
                                    <option value={60000} className="bg-slate-800">1 minute</option>
                                </select>
                            </div>
                        )}
                    </div>
                </div>

                {/* General Settings */}
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 mb-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <Globe className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-white">General Settings</h3>
                            <p className="text-sm text-gray-500">Basic site configuration</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Site Name</label>
                            <input
                                type="text"
                                value={settings.general.siteName}
                                onChange={(e) => handleChange('general', 'siteName', e.target.value)}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Admin Email</label>
                            <input
                                type="email"
                                value={settings.general.adminEmail}
                                onChange={(e) => handleChange('general', 'adminEmail', e.target.value)}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Timezone</label>
                            <select
                                value={settings.general.timezone}
                                onChange={(e) => handleChange('general', 'timezone', e.target.value)}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                            >
                                <option value="Asia/Colombo" className="bg-slate-800">Asia/Colombo (Sri Lanka)</option>
                                <option value="UTC" className="bg-slate-800">UTC</option>
                                <option value="Asia/Dubai" className="bg-slate-800">Asia/Dubai</option>
                                <option value="Asia/Singapore" className="bg-slate-800">Asia/Singapore</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm text-gray-400 mb-2">Language</label>
                            <select
                                value={settings.general.language}
                                onChange={(e) => handleChange('general', 'language', e.target.value)}
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500/50"
                            >
                                <option value="en" className="bg-slate-800">English</option>
                                <option value="si" className="bg-slate-800">Sinhala</option>
                                <option value="ta" className="bg-slate-800">Tamil</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-4">
                    <button
                        onClick={saveSettings}
                        className="flex-1 bg-gradient-to-r from-teal-500 to-blue-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-teal-400 hover:to-blue-400 transition-all shadow-lg shadow-teal-500/25 flex items-center justify-center gap-2"
                    >
                        {saved ? (
                            <>
                                <CheckCircle className="w-5 h-5" />
                                Saved!
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Save Settings
                            </>
                        )}
                    </button>
                    <button
                        onClick={resetToDefault}
                        className="px-6 py-3 bg-white/5 text-gray-400 rounded-xl hover:bg-white/10 transition-all flex items-center gap-2"
                    >
                        <RefreshCw className="w-5 h-5" />
                        Reset to Default
                    </button>
                </div>
            </div>
        </AdminLayout>
    );
};

export default SettingsPage;
